import {
  type Brand,
  BrandStatus,
  type BrandsApiGetAllBrandsRequest
} from '@innovationdepartment/proxima-sdk-axios';
import { BRANDS_LIST_PAGE_LIMIT } from 'constants/defaults';
import { useRef, useState } from 'react';
import useProximaSDK from './useProximaSDK';

type FetchBrand = {
  search?: string;
  plans?: string;
  spend?: string;
  status?:
    | BrandStatus
    | `${BrandStatus},${BrandStatus}`
    | `${BrandStatus},${BrandStatus},${BrandStatus}`
    | '';
};

type BrandRequest = {
  direction: 'next' | 'previous';
  page?: number;
} & FetchBrand;

export default function useBrandSearchApi() {
  const [pagination, setPagination] = useState({
    pageNumber: 0, // brand pages in api start at 1
    totalPages: 1,
    hasNext: true,
    hasPrevious: false
  });
  const [loading, setLoading] = useState(false);
  /* The latest request's failure; null while it runs and after it succeeds */
  const [error, setError] = useState<unknown>(null);
  const latestRequest = useRef(0);
  const brandsApi = useProximaSDK('BrandsApi');

  const handleBrandsRequest = async (params: BrandRequest) => {
    let brands: Brand[] = [];
    const { direction = 'next', page, search, plans, spend, status } = params;
    const isRequestingNext = direction === 'next';

    const pageNumber = page ?? pagination.pageNumber;

    /* make sure we don't go past the range of pages */
    const pageToFetch = isRequestingNext
      ? Math.min(pageNumber + 1, pagination.totalPages)
      : Math.max(pageNumber - 1, 1);

    const requestParams: BrandsApiGetAllBrandsRequest = {
      pageSize: BRANDS_LIST_PAGE_LIMIT,
      pageNumber: pageToFetch,
      search,
      plans,
      spend,
      status: status ?? BrandStatus.Active
    };

    const requestId = ++latestRequest.current;
    try {
      setLoading(true);
      setError(null);
      const response = await brandsApi.getAllBrands(requestParams);

      const { data, total = BRANDS_LIST_PAGE_LIMIT } = response.data;

      const totalPages = Math.ceil(total / BRANDS_LIST_PAGE_LIMIT) || 1;

      brands = data as Brand[];

      /* booleans that help which buttons to show in pagination */
      const hasNext = pageToFetch < totalPages;
      const hasPrevious = pageToFetch > 1;

      const nextPagination = {
        pageNumber: pageToFetch,
        totalPages,
        hasNext,
        hasPrevious
      };

      setPagination(nextPagination);
    } catch (caught) {
      if (requestId === latestRequest.current) setError(caught);
    }

    setLoading(false);
    return brands;
  };

  const getBrands = async (params: FetchBrand = {}) =>
    handleBrandsRequest({ ...params, direction: 'next', page: 0 });

  const getNextBrandBatch = async (params: FetchBrand = {}) =>
    handleBrandsRequest({ ...params, direction: 'next' });

  const getPreviousBrandBatch = async (params: FetchBrand = {}) =>
    handleBrandsRequest({ ...params, direction: 'previous' });

  const { hasNext, hasPrevious } = pagination;

  return {
    loading,
    error,
    hasNext,
    hasPrevious,
    getBrands,
    getNextBrandBatch,
    getPreviousBrandBatch
  };
}
